import Header from '@/components/Header';
import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { SeverityChip } from '@/components/SeverityChip';
import { PlusCircle, Trash2, Users, Radio, Activity, DollarSign, Shield, RefreshCw, CheckCircle2, Building2, Truck, XCircle, AlertTriangle, Monitor, Copy, Search, Send, Megaphone, Bold, Italic, Link2, List, Heading2, Eye, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';

export default function Admin() {
  const [tab, setTab] = useState('overview');
  const [reviewCount, setReviewCount] = useState(0);
  const loadReviewCount = () => api.get('/admin/alerts/review/count').then((r) => setReviewCount(r.data.count)).catch(() => {});
  useEffect(() => { loadReviewCount(); }, [tab]);
  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-6 h-6 text-orange-600" />
          <h1 className="font-display font-black text-3xl md:text-4xl">Admin Console</h1>
        </div>
        <p className="text-zinc-600 mb-6">Manage the platform, broadcast alerts and monitor system health.</p>

        <EmailHealthBanner />

        <div className="flex flex-wrap gap-1 mb-6 border-b border-zinc-200">
          <TabButton on={tab === 'overview'} onClick={() => setTab('overview')} icon={<Activity className="w-4 h-4" />} label="Overview" testid="admin-tab-overview" />
          <TabButton on={tab === 'broadcast'} onClick={() => setTab('broadcast')} icon={<Radio className="w-4 h-4" />} label="Broadcast Alert" testid="admin-tab-broadcast" />
          <TabButton on={tab === 'campaigns'} onClick={() => setTab('campaigns')} icon={<Megaphone className="w-4 h-4" />} label="Campaigns" testid="admin-tab-campaigns" />
          <TabButton on={tab === 'users'} onClick={() => setTab('users')} icon={<Users className="w-4 h-4" />} label="Users" testid="admin-tab-users" />
          <TabButton on={tab === 'pilots'} onClick={() => setTab('pilots')} icon={<Truck className="w-4 h-4" />} label="Load Pilots" testid="admin-tab-pilots" />
          <TabButton on={tab === 'review'} onClick={() => setTab('review')} icon={<AlertTriangle className="w-4 h-4" />} label={`Review${reviewCount ? ` (${reviewCount})` : ''}`} testid="admin-tab-review" />
          <TabButton on={tab === 'pricing'} onClick={() => setTab('pricing')} icon={<DollarSign className="w-4 h-4" />} label="SMS Pricing" testid="admin-tab-pricing" />
          <TabButton on={tab === 'orgs'} onClick={() => setTab('orgs')} icon={<Building2 className="w-4 h-4" />} label="Organisations" testid="admin-tab-orgs" />
          <TabButton on={tab === 'screens'} onClick={() => setTab('screens')} icon={<Monitor className="w-4 h-4" />} label="NOC Screens" testid="admin-tab-screens" />
          <TabButton on={tab === 'seo'} onClick={() => setTab('seo')} icon={<Search className="w-4 h-4" />} label="SEO / Search Console" testid="admin-tab-seo" />
        </div>

        {tab === 'overview' && <Overview />}
        {tab === 'broadcast' && <Broadcast />}
        {tab === 'campaigns' && <CampaignsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'pilots' && <LoadPilotsTab />}
        {tab === 'review' && <ReviewTab onChange={loadReviewCount} />}
        {tab === 'pricing' && <Pricing />}
        {tab === 'orgs' && <OrgsTab />}
        {tab === 'screens' && <NocScreensTab />}
        {tab === 'seo' && <SeoGuideTab />}
      </div>
    </div>
  );
}

function NocScreensTab() {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(365);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [pairLabel, setPairLabel] = useState('');
  const [approving, setApproving] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const load = async () => {
    try { const { data } = await api.get('/admin/noc-screens'); setScreens(data || []); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setLoading(true);
    try {
      await api.post('/admin/noc-screens', { label: label || 'Screen', days: parseInt(days, 10) || 365 });
      setLabel('');
      toast.success('Screen link created');
      load();
    } catch { toast.error('Could not create link'); }
    finally { setLoading(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this link? The TV using it will be signed out.')) return;
    try { await api.post(`/admin/noc-screens/${id}/revoke`); toast.success('Link revoked'); load(); }
    catch { toast.error('Revoke failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this screen link permanently?')) return;
    try { await api.delete(`/admin/noc-screens/${id}`); load(); }
    catch { toast.error('Delete failed'); }
  };

  const approve = async () => {
    const c = code.replace(/\D/g, '');
    if (c.length !== 6) { toast.error('Enter the 6-digit code shown on the screen'); return; }
    setApproving(true);
    try {
      await api.post('/admin/noc/pair/approve', { code: c, label: pairLabel || 'Paired screen', days: parseInt(days, 10) || 365 });
      setCode(''); setPairLabel('');
      toast.success('Screen approved — it will connect shortly');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve code');
    } finally { setApproving(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Link copied'),
      () => toast.error('Copy failed — select and copy manually')
    );
  };

  return (
    <div className="space-y-4" data-testid="screens-tab">
      <div className="card">
        <div className="flex items-center gap-2 mb-1">
          <Monitor className="w-5 h-5 text-teal-600" />
          <h2 className="font-display font-bold text-lg">NOC / CCTV wall screens</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          Sign an unattended wall TV into the gated NOC &amp; CCTV screens <strong>without the Turnstile
          login form</strong>. Either create a link here and open it on the TV, or pair with the 6-digit
          code the TV shows. Each link is a read-only Business account and can be revoked any time.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border border-zinc-200 p-3">
            <div className="font-semibold text-sm mb-2">Create a link</div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm flex-1 min-w-[8rem]">
                <span className="block text-zinc-500 mb-1">Name</span>
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Front NOC TV" data-testid="screens-label" className="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              </label>
              <label className="text-sm">
                <span className="block text-zinc-500 mb-1">Days</span>
                <input type="number" min="1" max="3650" value={days} onChange={(e) => setDays(e.target.value)} data-testid="screens-days" className="w-24 border border-zinc-300 rounded-lg px-3 py-2" />
              </label>
              <button onClick={create} disabled={loading} data-testid="screens-generate" className="btn btn-primary">
                {loading ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
          <div className="rounded-lg border border-zinc-200 p-3">
            <div className="font-semibold text-sm mb-2">Approve a screen code</div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="block text-zinc-500 mb-1">6-digit code</span>
                <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={7} placeholder="000000" data-testid="screens-pair-code" className="w-28 border border-zinc-300 rounded-lg px-3 py-2 font-mono tracking-widest" />
              </label>
              <label className="text-sm flex-1 min-w-[8rem]">
                <span className="block text-zinc-500 mb-1">Name (optional)</span>
                <input value={pairLabel} onChange={(e) => setPairLabel(e.target.value)} placeholder="e.g. Ops room wall" data-testid="screens-pair-label" className="w-full border border-zinc-300 rounded-lg px-3 py-2" />
              </label>
              <button onClick={approve} disabled={approving} data-testid="screens-pair-approve" className="btn btn-primary">
                {approving ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card" data-testid="screens-list">
        <div className="font-semibold text-sm mb-3">Active &amp; revoked links ({screens.length})</div>
        {screens.filter((s) => { const e = s.active && screenExpiry(s); return e && e.daysLeft <= 14; }).length > 0 && (
          <div className="mb-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="screens-expiry-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Some screen links expire within 14 days — create a fresh link and open it on the TV before the old one lapses, so walls never drop offline.</span>
          </div>
        )}
        {screens.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">No screen links yet.</div>}
        <div className="space-y-3">
          {screens.map((s) => {
            const e = screenExpiry(s);
            return (
            <div key={s.id} className="rounded-lg border border-zinc-200 p-3" data-testid={`screen-${s.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <span className="font-semibold text-sm">{s.label || 'Screen'}</span>
                  <span className={`ml-2 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${s.active ? 'bg-teal-100 text-teal-700' : 'bg-zinc-200 text-zinc-500'}`}>
                    {s.active ? 'Active' : 'Revoked'}
                  </span>
                  <span className="ml-2 text-xs text-zinc-400">created {s.created_at ? dayjs(s.created_at).format('D MMM YYYY') : '—'}</span>
                  {s.active && e && (
                    <span className={`ml-2 text-xs font-semibold ${e.expired ? 'text-red-600' : e.daysLeft <= 14 ? 'text-amber-600' : 'text-zinc-400'}`} data-testid={`screen-expiry-${s.id}`}>
                      {e.expired ? 'expired' : `expires in ${e.daysLeft}d`}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {s.active && <button onClick={() => revoke(s.id)} className="btn btn-ghost text-red-600" data-testid={`screen-revoke-${s.id}`} title="Revoke"><XCircle className="w-4 h-4" /></button>}
                  <button onClick={() => del(s.id)} className="btn btn-ghost" data-testid={`screen-delete-${s.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {s.active && (
                <div className="space-y-2">
                  <LinkRow label="CCTV wall" url={origin + s.paths.cctv} onCopy={copy} testid={`screen-link-cctv-${s.id}`} />
                  <LinkRow label="NOC overview" url={origin + s.paths.noc} onCopy={copy} testid={`screen-link-noc-${s.id}`} />
                </div>
              )}
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}

function screenExpiry(s) {
  if (!s.created_at || !s.days) return null;
  const exp = dayjs(s.created_at).add(s.days, 'day');
  return { exp, daysLeft: Math.ceil(exp.diff(dayjs(), 'hour') / 24), expired: exp.isBefore(dayjs()) };
}

function LinkRow({ label, url, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-zinc-500 w-28 shrink-0">{label}</span>
      <input readOnly value={url} data-testid={testid} onFocus={(e) => e.target.select()} className="flex-1 border border-zinc-300 rounded-lg px-3 py-2 text-xs font-mono bg-zinc-50" />
      <button onClick={() => onCopy(url)} className="btn btn-ghost shrink-0" data-testid={`${testid}-copy`} title="Copy link">
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

function SeoStep({ n, title, children }) {
  return (
    <li className="flex gap-3">
      <span className="w-7 h-7 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center shrink-0">{n}</span>
      <div className="text-sm text-zinc-700 pt-0.5">{title}{children}</div>
    </li>
  );
}

function SeoGuideTab() {
  const site = 'https://alerts.pttnz.net';
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success('Copied'), () => {});
  const Step = SeoStep;
  return (
    <div className="space-y-4" data-testid="seo-tab">
      <div className="card">
        <div className="flex items-center gap-2 mb-1">
          <Search className="w-5 h-5 text-teal-600" />
          <h2 className="font-display font-bold text-lg">Get found on Google faster</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          The site already ships a sitemap, per-page titles and structured data. Do this once in
          Google Search Console so Google crawls and ranks the pages sooner. It can still take a few
          days to a couple of weeks for changes to show in search.
        </p>
        <ol className="space-y-4">
          <Step n="1" title="Open Google Search Console">
            {' — '}<a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-teal-700 underline">search.google.com/search-console</a>, sign in with your company Google account.
          </Step>
          <Step n="2" title="Add a property and verify ownership">
            {' — choose “URL prefix” and enter '}<code className="bg-zinc-100 px-1 rounded">{site}</code>. Verify with the DNS TXT record or the HTML file method (your hosting/DNS provider).
          </Step>
          <Step n="3" title="Submit the sitemap">
            {' — under Indexing → Sitemaps, enter '}<code className="bg-zinc-100 px-1 rounded">api/sitemap.xml</code>{' and Submit. It updates itself as new alerts appear.'}
            <div className="mt-2"><LinkRow label="Sitemap" url={`${site}/api/sitemap.xml`} onCopy={copy} testid="seo-sitemap" /></div>
          </Step>
          <Step n="4" title="Request indexing for key pages">
            {' — paste each URL into the top search bar, then click “Request indexing”:'}
            <div className="mt-2 space-y-2">
              {['/', '/alerts', '/map', '/oversize'].map((p) => (
                <LinkRow key={p} label={p === '/' ? 'Home' : p} url={site + p} onCopy={copy} testid={`seo-url-${p === '/' ? 'home' : p.slice(1)}`} />
              ))}
            </div>
          </Step>
          <Step n="5" title="Check coverage weekly">
            {' — the Pages and Performance reports show which pages are indexed and which search terms bring visitors. Fix anything flagged as “Excluded”.'}
          </Step>
        </ol>
      </div>
      <div className="card">
        <div className="font-semibold text-sm mb-2">Tips for ranking on your keywords</div>
        <ul className="text-sm text-zinc-700 space-y-1.5 list-disc pl-5">
          <li>Keep the business name consistent everywhere as <strong>“PTT NZ Alerts”</strong> (website, Google Business Profile, social, directory listings).</li>
          <li>Create a free <strong>Google Business Profile</strong> for PTT NZ Limited — it strongly boosts branded searches.</li>
          <li>Get a few inbound links (industry directories, partners, NZTA/pilot associations) pointing at the site.</li>
          <li>Individual closures now have their own titles &amp; structured data, so specific incident searches can surface an alert page.</li>
        </ul>
      </div>
    </div>
  );
}


function EmailHealthBanner() {
  const [h, setH] = useState(null);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { api.get('/admin/email/health').then((r) => setH(r.data)).catch(() => {}); }, []);
  if (!h) return null;
  const { status, provider, secret_expires, days_until_expiry, sender } = h;

  const alert = {
    expired: { bg: '#fee2e2', bd: '#fca5a5', fg: '#991b1b', text: `Microsoft 365 client secret expired ${Math.abs(days_until_expiry)} day(s) ago — renew it in Azure and update M365_CLIENT_SECRET.` },
    warning: { bg: '#fef3c7', bd: '#fcd34d', fg: '#92400e', text: `Microsoft 365 client secret expires in ${days_until_expiry} day(s) (${secret_expires}) — renew it in Azure soon.` },
    none: { bg: '#fee2e2', bd: '#fca5a5', fg: '#991b1b', text: 'No email provider configured — no emails will send until Microsoft 365 or SMTP is set.' },
  }[status] || null;
  const nudge = status === 'ok' && provider === 'microsoft-graph' && !secret_expires;

  const send = async () => {
    setSending(true); setResult(null);
    try {
      const { data } = await api.post('/admin/email/test', to.trim() ? { to: to.trim() } : {});
      setResult(data.ok
        ? { ok: true, msg: `Accepted by ${data.provider} for ${data.to}. If it doesn't arrive, it's a delivery/DNS issue (SPF/DKIM/DMARC or a restricted sender in M365) — not the app.` }
        : { ok: false, msg: `Send failed via ${data.provider}. Check the backend logs.` });
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Send failed.' });
    } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-email-health" className="card mb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Send className="w-4 h-4 text-teal-600" />
          <span className="font-semibold">Email delivery</span>
          <span className="chip text-xs" style={{ background: provider === 'microsoft-graph' ? '#e0f2fe' : provider === 'smtp' ? '#f4f4f5' : '#fee2e2' }}>
            {provider === 'microsoft-graph' ? 'Microsoft 365 (Graph)' : provider === 'smtp' ? 'SMTP' : 'Not configured'}
          </span>
          {sender && <span className="text-xs text-zinc-500">· {sender}</span>}
        </div>
        <div className="flex items-center gap-2">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder={sender || 'you@example.com'} className="input py-1.5 text-sm w-56" data-testid="email-test-to" />
          <button onClick={send} disabled={sending} className="btn btn-primary" data-testid="email-test-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
          </button>
        </div>
      </div>
      {alert && (
        <div className="mt-3 rounded-lg border p-2.5 text-sm flex items-start gap-2" style={{ background: alert.bg, borderColor: alert.bd, color: alert.fg }} data-testid="email-secret-alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{alert.text}</span>
        </div>
      )}
      {nudge && <div className="mt-3 text-xs text-zinc-500">Tip: set <code>M365_SECRET_EXPIRES</code> to get client-secret renewal reminders here.</div>}
      {result && (
        <div className="mt-3 rounded-lg p-2.5 text-sm flex items-start gap-2" style={{ background: result.ok ? '#ecfdf5' : '#fee2e2', color: result.ok ? '#065f46' : '#991b1b' }} data-testid="email-test-result">
          {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />} <span>{result.msg}</span>
        </div>
      )}
    </div>
  );
}


function TabButton({ on, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition -mb-px ${on ? 'border-orange-500 text-orange-700' : 'border-transparent text-zinc-500 hover:text-zinc-900'}`}>
      {icon}{label}
    </button>
  );
}

/* ---------- Stale-alert review ---------- */
const REVIEW_REASON = {
  gone_from_feed_no_end_date: 'Gone from live feed (no end date)',
  gone_from_feed_future_end: 'Gone from live feed (end date not yet reached)',
  planned_past_end_still_in_feed: 'Planned event past its end date',
};

function ReviewTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/alerts/review'); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const resolve = async (id) => {
    try { await api.post(`/admin/alerts/${id}/review-resolve`); toast.success('Alert marked resolved'); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const keep = async (id) => {
    try { await api.post(`/admin/alerts/${id}/review-keep`); toast.success('Kept active'); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-4" data-testid="review-tab">
      <div className="card">
        <h3 className="font-display font-bold text-lg mb-1">Stale-alert review</h3>
        <p className="text-sm text-zinc-500">Events the poller suspects have finished but couldn't confirm automatically — either they dropped out of the live feed without an end date, or a planned event is past its end date. Confirm each as <strong>Resolve</strong> or <strong>Keep active</strong>.</p>
      </div>
      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="card text-center py-10 text-zinc-500" data-testid="review-empty">Nothing to review — all events reconciled.</div>
      ) : items.map((a) => (
        <div key={a.id} className="card flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`review-${a.id}`}>
          <SeverityChip severity={a.severity} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{a.event_type} · {a.highway || a.location_area}</div>
            <div className="text-xs text-zinc-500 truncate">{a.location_area}</div>
            <div className="text-xs mt-1.5 flex flex-wrap items-center gap-2">
              <span className="chip chip-outline-orange text-[10px]">{REVIEW_REASON[a.review_reason] || 'Needs review'}</span>
              {a.end_date && <span className="text-zinc-400">Ends {dayjs(a.end_date).format('D MMM HH:mm')}</span>}
              {a.planned && <span className="chip chip-outline-teal text-[10px]">Planned</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => resolve(a.id)} className="btn btn-secondary" data-testid={`review-resolve-${a.id}`}><CheckCircle2 className="w-4 h-4" /> Resolve</button>
            <button onClick={() => keep(a.id)} className="btn btn-ghost" data-testid={`review-keep-${a.id}`}>Keep active</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Organisations ---------- */
function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ name: '', seat_cap: 10, owner_email: '' });
  const [saving, setSaving] = useState(false);
  const load = async () => { const { data } = await api.get('/admin/orgs'); setOrgs(data); };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/admin/orgs', { ...form, seat_cap: parseInt(form.seat_cap) });
      toast.success('Organisation created'); setForm({ name: '', seat_cap: 10, owner_email: '' }); load();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="card max-w-2xl" data-testid="org-create-form">
        <h3 className="font-display font-bold text-lg mb-1">Create a Business organisation</h3>
        <p className="text-sm text-zinc-500 mb-4">The owner (must already have an account) can invite staff up to the seat cap. All members get the Business plan.</p>
        <div className="grid md:grid-cols-3 gap-4">
          <div><label className="label">Company name</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="org-name" /></div>
          <div><label className="label">Seats</label><input type="number" min="2" className="input" value={form.seat_cap} onChange={(e) => setForm({ ...form, seat_cap: e.target.value })} required data-testid="org-seats" /><p className="text-[11px] text-zinc-400 mt-1">Owner counts as 1 seat.</p></div>
          <div><label className="label">Owner email</label><input type="email" className="input" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} required data-testid="org-owner" /></div>
        </div>
        <button className="btn btn-primary mt-4" disabled={saving} data-testid="org-create-btn"><Building2 className="w-4 h-4" /> {saving ? 'Creating…' : 'Create organisation'}</button>
      </form>
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500"><tr>
            <th className="text-left px-4 py-3">Organisation</th><th className="text-left px-4 py-3">Owner</th><th className="text-left px-4 py-3">Seats</th><th className="text-left px-4 py-3">Created</th></tr></thead>
          <tbody>
            {orgs.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-500">No organisations yet.</td></tr>}
            {orgs.map((o) => (
              <tr key={o.id} className="border-t border-zinc-100" data-testid={`org-row-${o.id}`}>
                <td className="px-4 py-3 font-semibold">{o.name}</td>
                <td className="px-4 py-3 text-zinc-600">{o.owner_email}</td>
                <td className="px-4 py-3">{o.member_count} / {o.seat_cap}</td>
                <td className="px-4 py-3 text-zinc-500">{o.created_at ? dayjs(o.created_at).format('D MMM YY') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview() {
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [manuals, setManuals] = useState([]);

  const load = async () => {
    const [h, s, a] = await Promise.all([
      api.get('/admin/health'),
      api.get('/alerts/stats'),
      api.get('/alerts?source=manual&limit=20'),
    ]);
    setHealth(h.data); setStats(s.data);
    setManuals(a.data);
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const pollNow = async () => {
    try {
      const { data } = await api.post('/admin/nzta/poll-now');
      toast.success(`NZTA feed OK · ${data.features} features`);
      load();
    } catch (e) { toast.error('Poll failed'); }
  };

  const resolveManual = async (id) => {
    await api.patch(`/admin/alerts/${id}/status?status=Resolved`);
    toast.success('Marked as resolved');
    load();
  };

  const deleteManual = async (id) => {
    if (!window.confirm('Delete this manual alert?')) return;
    await api.delete(`/admin/alerts/${id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MiniStat label="Total Users" value={health?.users ?? '—'} tint="#2DD4B0" />
        <MiniStat label="Total Alerts" value={health?.alerts ?? '—'} tint="#F7941D" />
        <MiniStat label="Manual Alerts" value={health?.manual_alerts ?? '—'} tint="#E11D48" />
        <MiniStat label="Push Subs" value={health?.push_subscriptions ?? '—'} tint="#FBBF24" />
      </div>

      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">NZTA Ingest Health</h3>
          <button onClick={pollNow} className="btn btn-ghost" data-testid="admin-poll-now"><RefreshCw className="w-4 h-4" /> Poll now</button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <Kv k="Status" v={<span className={`chip ${health?.ingest?.status === 'ok' ? 'chip-outline-yellow' : 'chip-outline-red'}`} style={{ background: health?.ingest?.status === 'ok' ? '#dcfce7' : '', color: health?.ingest?.status === 'ok' ? '#166534' : '', borderColor: health?.ingest?.status === 'ok' ? '#4ade80' : '' }}>{health?.ingest?.status || 'idle'}</span>} />
          <Kv k="Last poll" v={health?.ingest?.last_poll ? dayjs(health.ingest.last_poll).format('HH:mm:ss') : '—'} />
          <Kv k="Features" v={health?.ingest?.total_features ?? '—'} />
          <Kv k="Last new" v={health?.ingest?.last_new ?? '—'} />
        </div>
        {stats && (
          <div className="mt-4 pt-4 border-t border-zinc-100 flex gap-3">
            <div className="chip chip-red">{stats.by_severity.red} critical</div>
            <div className="chip chip-orange">{stats.by_severity.orange} urgent</div>
            <div className="chip chip-yellow">{stats.by_severity.yellow} info</div>
            <div className="chip chip-outline-orange">{stats.highways_affected} highways</div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="font-display font-bold text-lg mb-4">Recent Manual Broadcasts</h3>
        {manuals.length === 0 ? <p className="text-sm text-zinc-500">No manual alerts yet. Use the Broadcast tab to create one.</p> : (
          <div className="space-y-2">
            {manuals.map((a) => {
              const resolved = (a.status || '').toLowerCase() === 'resolved';
              return (
              <div key={a.id} className={`flex items-center gap-3 p-3 rounded-lg border border-zinc-100 ${resolved ? 'opacity-60' : ''}`}>
                <SeverityChip severity={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{a.event_type} · {a.highway || a.location_area}</div>
                  <div className="text-xs text-zinc-500 truncate">by {a.created_by} · {dayjs(a.created_at).format('D MMM HH:mm')}</div>
                </div>
                <span className={`chip text-[10px] ${resolved ? 'chip-outline-yellow' : 'chip-outline-red'}`} style={resolved ? { background: '#dcfce7', color: '#166534', borderColor: '#4ade80' } : {}}>{resolved ? 'Resolved' : 'Active'}</span>
                {!resolved && (
                  <button onClick={() => resolveManual(a.id)} className="p-2 hover:bg-teal-50 text-teal-600 rounded-lg" title="Mark resolved" data-testid={`admin-resolve-${a.id}`}><CheckCircle2 className="w-4 h-4" /></button>
                )}
                <button onClick={() => deleteManual(a.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg" data-testid={`admin-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tint }) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</div>
      <div className="font-display font-black text-3xl mt-2" style={{ color: tint }}>{value}</div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase text-zinc-500 tracking-wider mb-1">{k}</div>
      <div className="font-semibold text-zinc-900">{v}</div>
    </div>
  );
}

/* ---------- Broadcast ---------- */
function Broadcast() {
  const [form, setForm] = useState({
    event_type: 'Road Closure',
    severity: 'red',
    highway: '',
    island: 'north',
    location_area: '',
    description: '',
    alternative_route: '',
    expected_resolution: '',
    broadcast: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/admin/alerts', form);
      toast.success(`Alert created${form.broadcast ? ' & broadcast' : ''}: ${data.event_type}`);
      setForm({ ...form, location_area: '', description: '', alternative_route: '', expected_resolution: '' });
    } catch (e) {
      toast.error('Failed to create');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="card max-w-3xl">
      <h3 className="font-display font-bold text-xl mb-1">Broadcast a manual alert</h3>
      <p className="text-sm text-zinc-500 mb-6">This will appear in the portal and (if broadcast is on) push to all subscribed users matching the severity + highway.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label">Event type</label>
          <select className="input" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="bc-type">
            {['Road Closure', 'Crash', 'Hazard', 'Roadworks', 'Weather', 'Detour', 'Other'].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Severity</label>
          <select className="input" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="bc-severity">
            <option value="red">RED · Critical (closures)</option>
            <option value="orange">ORANGE · Urgent</option>
            <option value="yellow">YELLOW · Info</option>
          </select>
        </div>
        <div>
          <label className="label">Highway (e.g., SH1)</label>
          <input className="input" value={form.highway} onChange={(e) => setForm({ ...form, highway: e.target.value.toUpperCase() })} placeholder="SH1" data-testid="bc-highway" />
        </div>
        <div>
          <label className="label">Island</label>
          <select className="input" value={form.island} onChange={(e) => setForm({ ...form, island: e.target.value })} data-testid="bc-island">
            <option value="north">🌋 North Island</option>
            <option value="south">🏔️ South Island</option>
          </select>
        </div>
        <div>
          <label className="label">Expected resolution</label>
          <input className="input" value={form.expected_resolution} onChange={(e) => setForm({ ...form, expected_resolution: e.target.value })} placeholder="e.g., 18:00 tonight" data-testid="bc-etr" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Location (be specific)</label>
          <input className="input" required value={form.location_area} onChange={(e) => setForm({ ...form, location_area: e.target.value })} placeholder="e.g., SH1 Ngauranga to Tawa, Northbound" data-testid="bc-location" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Description</label>
          <textarea className="input" rows={4} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened, current impact, driver advice…" data-testid="bc-desc" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Alternative route (optional)</label>
          <input className="input" value={form.alternative_route} onChange={(e) => setForm({ ...form, alternative_route: e.target.value })} data-testid="bc-altroute" />
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <input type="checkbox" checked={form.broadcast} onChange={(e) => setForm({ ...form, broadcast: e.target.checked })} className="w-5 h-5 accent-orange-500" data-testid="bc-broadcast" id="broadcast" />
          <label htmlFor="broadcast" className="text-sm font-medium">Push to subscribers immediately (email + push + in-portal)</label>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn btn-primary" disabled={saving} data-testid="bc-submit">
          <PlusCircle className="w-4 h-4" /> {saving ? 'Creating…' : 'Create alert'}
        </button>
      </div>
    </form>
  );
}

/* ---------- Campaigns (newsletters / announcements) ---------- */
const AUDIENCE_HELP = {
  all: 'Everyone with an account who has not opted out of marketing email.',
  role: 'Only users with the selected role (user or admin).',
  plan: 'Only users on the selected subscription plan.',
  segment: 'A specific group — load pilots or business users.',
  island: 'Only users subscribed to at least one highway on the selected island.',
};
const AUDIENCE_LABEL = { all: 'All users', role: 'By role', plan: 'By plan', segment: 'By segment', island: 'By island' };

function CampaignsTab() {
  const { user } = useAuth();
  const [mode, setMode] = useState('rich');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audType, setAudType] = useState('all');
  const [role, setRole] = useState('user');
  const [plan, setPlan] = useState('business');
  const [segment, setSegment] = useState('load_pilots');
  const [island, setIsland] = useState('north');
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const editorRef = useRef(null);

  useEffect(() => { if (user?.email && !testEmail) setTestEmail(user.email); }, [user]); // eslint-disable-line

  const loadHistory = () => api.get('/admin/campaigns').then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  // When switching into rich mode, hydrate the editor once from state.
  useEffect(() => {
    if (mode === 'rich' && editorRef.current && editorRef.current.innerHTML !== bodyHtml) {
      editorRef.current.innerHTML = bodyHtml || '';
    }
  }, [mode]); // eslint-disable-line

  const audience = () => ({
    type: audType,
    ...(audType === 'role' ? { role } : {}),
    ...(audType === 'plan' ? { plan } : {}),
    ...(audType === 'segment' ? { segment } : {}),
    ...(audType === 'island' ? { island } : {}),
  });

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    setBodyHtml(editorRef.current?.innerHTML || '');
  };
  const addLink = () => {
    const url = window.prompt('Link URL (include https://)');
    if (url) exec('createLink', url);
  };

  const preview = async () => {
    setCounting(true);
    try {
      const { data } = await api.post('/admin/campaigns/audience-preview', audience());
      setCount(data.count);
    } catch { toast.error('Could not fetch audience'); }
    finally { setCounting(false); }
  };
  useEffect(() => { setCount(null); }, [audType, role, plan, segment, island]);

  const validate = () => {
    if (!subject.trim()) { toast.error('Add a subject line'); return false; }
    if (!bodyHtml.trim()) { toast.error('Add some message content'); return false; }
    return true;
  };

  const sendTest = async () => {
    if (!validate()) return;
    if (!testEmail.trim()) { toast.error('Enter a test email address'); return; }
    setTesting(true);
    try {
      await api.post('/admin/campaigns/test', { subject, body_html: bodyHtml, content_mode: mode, preheader, audience: audience(), test_email: testEmail.trim() });
      toast.success(`Test sent to ${testEmail.trim()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test send failed'); }
    finally { setTesting(false); }
  };

  const send = async () => {
    if (!validate()) return;
    const n = count;
    const msg = n != null ? `Send "${subject}" to ${n} recipient${n === 1 ? '' : 's'}?` : `Send "${subject}" now? Use "Preview recipients" first to see how many will receive it.`;
    if (!window.confirm(msg)) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/campaigns/send', { subject, body_html: bodyHtml, content_mode: mode, preheader, audience: audience() });
      toast.success(`Campaign queued — sending to ${data.campaign.total} recipient${data.campaign.total === 1 ? '' : 's'}`);
      setSubject(''); setPreheader(''); setBodyHtml('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      setCount(null);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this campaign from history?')) return;
    try { await api.delete(`/admin/campaigns/${id}`); loadHistory(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-tab">
      <div className="card bg-orange-50/50 border-orange-200">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="w-5 h-5 text-orange-600" />
          <h2 className="font-display font-bold text-lg">Email campaigns</h2>
        </div>
        <p className="text-sm text-orange-900">
          Send newsletters and feature announcements to your users. Every email includes a one-click
          unsubscribe (required by NZ anti-spam law) and skips anyone who has opted out. Sends go out via
          your SMTP inbox, gradually — large lists can take a few minutes.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Compose */}
        <div className="lg:col-span-3 card space-y-4">
          <div>
            <label className="label">Subject</label>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. New: CCTV NOC wall for your team" data-testid="campaign-subject" />
          </div>
          <div>
            <label className="label">Preview text <span className="text-zinc-400 font-normal">(optional — the grey snippet shown in inboxes)</span></label>
            <input className="input" value={preheader} onChange={(e) => setPreheader(e.target.value)} placeholder="A short teaser line…" data-testid="campaign-preheader" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Message</label>
              <div className="flex gap-1 text-xs">
                <button type="button" onClick={() => { if (mode === 'html') setBodyHtml(editorRef.current?.innerHTML || bodyHtml); setMode('rich'); }} className={`px-2 py-1 rounded font-semibold ${mode === 'rich' ? 'bg-orange-100 text-orange-700' : 'text-zinc-500'}`} data-testid="campaign-mode-rich">Rich text</button>
                <button type="button" onClick={() => setMode('html')} className={`px-2 py-1 rounded font-semibold ${mode === 'html' ? 'bg-orange-100 text-orange-700' : 'text-zinc-500'}`} data-testid="campaign-mode-html">HTML</button>
              </div>
            </div>

            {mode === 'rich' ? (
              <div className="border border-zinc-300 rounded-lg overflow-hidden">
                <div className="flex items-center gap-1 border-b border-zinc-200 bg-zinc-50 px-2 py-1.5">
                  <ToolBtn onClick={() => exec('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec('formatBlock', 'H2')} title="Heading"><Heading2 className="w-4 h-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bullet list"><List className="w-4 h-4" /></ToolBtn>
                  <ToolBtn onClick={addLink} title="Link"><Link2 className="w-4 h-4" /></ToolBtn>
                </div>
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={() => setBodyHtml(editorRef.current?.innerHTML || '')}
                  className="min-h-[220px] px-4 py-3 text-sm leading-relaxed focus:outline-none prose-sm"
                  data-testid="campaign-editor"
                />
              </div>
            ) : (
              <textarea className="input font-mono text-xs" rows={14} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} placeholder="<p>Your raw HTML…</p>" data-testid="campaign-html" />
            )}
          </div>

          <div className="flex flex-wrap items-end gap-2 border-t border-zinc-100 pt-4">
            <label className="text-sm flex-1 min-w-[12rem]">
              <span className="block text-zinc-500 mb-1">Send a test to</span>
              <input className="input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="campaign-test-email" />
            </label>
            <button onClick={sendTest} disabled={testing} className="btn btn-ghost" data-testid="campaign-send-test">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} {testing ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </div>

        {/* Audience + send */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card space-y-3">
            <div className="font-semibold text-sm">Audience</div>
            {['all', 'role', 'plan', 'segment', 'island'].map((t) => (
              <label key={t} className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="aud" checked={audType === t} onChange={() => setAudType(t)} className="mt-1 accent-orange-500" data-testid={`campaign-aud-${t}`} />
                <span>
                  <span className="font-medium">{AUDIENCE_LABEL[t]}</span>
                  <span className="block text-xs text-zinc-500">{AUDIENCE_HELP[t]}</span>
                </span>
              </label>
            ))}
            {audType === 'role' && (
              <select className="input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="campaign-role">
                <option value="user">Users</option>
                <option value="admin">Admins</option>
              </select>
            )}
            {audType === 'plan' && (
              <select className="input" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="campaign-plan">
                <option value="free">Free plan</option>
                <option value="plus">Plus plan</option>
                <option value="business">Business plan</option>
              </select>
            )}
            {audType === 'segment' && (
              <select className="input" value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="campaign-segment">
                <option value="load_pilots">Load pilots</option>
                <option value="business_users">Business users</option>
              </select>
            )}
            {audType === 'island' && (
              <select className="input" value={island} onChange={(e) => setIsland(e.target.value)} data-testid="campaign-island">
                <option value="north">North Island subscribers</option>
                <option value="south">South Island subscribers</option>
              </select>
            )}
            <button onClick={preview} disabled={counting} className="btn btn-ghost w-full" data-testid="campaign-preview">
              {counting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Preview recipients
            </button>
            {count != null && (
              <div className="text-center text-sm bg-teal-50 text-teal-800 rounded-lg py-2 font-semibold" data-testid="campaign-count">
                {count} recipient{count === 1 ? '' : 's'}
              </div>
            )}
          </div>
          <button onClick={send} disabled={sending} className="btn btn-primary w-full" data-testid="campaign-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? 'Queuing…' : 'Send campaign'}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="card p-0 overflow-hidden" data-testid="campaign-history">
        <div className="px-4 py-3 border-b border-zinc-100 font-semibold text-sm">Sent campaigns ({history.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Audience</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Sent</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No campaigns sent yet.</td></tr>}
              {history.map((c) => (
                <tr key={c.id} className="border-t border-zinc-100" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold max-w-[16rem] truncate">{c.subject}</td>
                  <td className="px-4 py-3 text-zinc-600">{c.audience_label}</td>
                  <td className="px-4 py-3">
                    <span className={`chip text-[10px] ${c.status === 'sent' ? 'chip-outline-teal' : c.status === 'sending' ? 'chip-outline-orange' : 'chip-outline-zinc'}`}>
                      {c.status === 'sending' ? 'sending…' : c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{c.sent}/{c.total}{c.failed ? ` · ${c.failed} failed` : ''}</td>
                  <td className="px-4 py-3 text-zinc-500">{c.created_at ? dayjs(c.created_at).format('D MMM HH:mm') : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`campaign-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ToolBtn({ onClick, title, children }) {
  return (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }} title={title} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-700">
      {children}
    </button>
  );
}

/* ---------- Load Pilots ---------- */
function LoadPilotsTab() {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/load-pilots${filter ? `?status=${filter}` : ''}`);
    setPilots(data); setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const decide = async (u, action) => {
    try {
      await api.post(`/admin/load-pilots/${u.id}/${action}`);
      toast.success(`${u.email} ${action === 'approve' ? 'approved' : 'rejected'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const badge = (s) => s === 'approved' ? <span className="chip" style={{ background: '#dcfce7', color: '#166534' }}>approved</span>
    : s === 'rejected' ? <span className="chip" style={{ background: '#fee2e2', color: '#991b1b' }}>rejected</span>
    : <span className="chip" style={{ background: '#fef9c3', color: '#854d0e' }}>pending</span>;

  return (
    <div>
      <div className="flex gap-2 mb-4" data-testid="pilots-filter">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button key={s || 'all'} onClick={() => setFilter(s)} className={`chip ${filter === s ? 'chip-outline-orange' : 'chip-outline-zinc'}`} data-testid={`pilots-filter-${s || 'all'}`}>{s || 'all'}</button>
        ))}
      </div>
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Pilot</th>
                <th className="text-left px-4 py-3">Class</th>
                <th className="text-left px-4 py-3">Pilot #</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Loading…</td></tr>}
              {!loading && pilots.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No load pilots{filter ? ` with status "${filter}"` : ''}.</td></tr>}
              {pilots.map((u) => (
                <tr key={u.id} className="border-t border-zinc-100" data-testid={`pilot-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{u.full_name || u.email.split('@')[0]}</div>
                    <div className="text-xs text-zinc-500">{u.email}{!u.email_verified && <span className="ml-1 text-orange-600">· email unverified</span>}</div>
                  </td>
                  <td className="px-4 py-3">{u.pilot_class || '—'}</td>
                  <td className="px-4 py-3">{u.pilot_number || '—'}</td>
                  <td className="px-4 py-3">{u.company || '—'}</td>
                  <td className="px-4 py-3">{badge(u.pilot_status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {u.pilot_status !== 'approved' && <button onClick={() => decide(u, 'approve')} className="btn btn-primary inline-flex items-center gap-1 !px-3 !py-1.5 text-xs" data-testid={`pilot-approve-${u.id}`}><CheckCircle2 className="w-4 h-4" />Approve</button>}
                      {u.pilot_status !== 'rejected' && <button onClick={() => decide(u, 'reject')} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`pilot-reject-${u.id}`}><XCircle className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------- Users ---------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { const { data } = await api.get('/admin/users'); setUsers(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    await api.patch(`/admin/users/${u.id}/role?role=${role}`);
    toast.success(`Role updated for ${u.email}`);
    load();
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const setPlan = async (u, plan) => {
    try { await api.patch(`/admin/users/${u.id}/plan?plan=${plan}`); toast.success(`Plan set to ${plan} for ${u.email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="card overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Threshold</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Highways</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Loading…</td></tr>}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{u.full_name || u.email.split('@')[0]}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`chip ${u.role === 'admin' ? 'chip-outline-red' : 'chip-outline-yellow'}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3"><SeverityChip severity={u.alert_threshold} outline /></td>
                <td className="px-4 py-3">
                  <select className="text-xs border border-zinc-200 rounded px-2 py-1 bg-white" value={u.plan || 'free'} onChange={(e) => setPlan(u, e.target.value)} data-testid={`plan-${u.id}`} disabled={u.role === 'admin'}>
                    <option value="free">Free</option>
                    <option value="plus">Plus</option>
                    <option value="business">Business</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-xs">{(u.subscribed_highways || []).slice(0, 4).map((k) => k.replace('N-', '').replace('S-', '') + (k.startsWith('N-') ? '·N' : '·S')).join(', ') || <span className="text-zinc-400">—</span>}{u.subscribed_highways?.length > 4 ? ` +${u.subscribed_highways.length - 4}` : ''}</td>
                <td className="px-4 py-3 text-zinc-500">{u.created_at ? dayjs(u.created_at).format('D MMM YY') : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setRole(u, u.role === 'admin' ? 'user' : 'admin')} className="text-xs font-semibold text-orange-600 hover:underline mr-3" data-testid={`toggle-role-${u.id}`}>
                    {u.role === 'admin' ? 'Demote' : 'Promote'}
                  </button>
                  <button onClick={() => del(u)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`del-user-${u.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Pricing ---------- */
function Pricing() {
  const [tiers, setTiers] = useState([]);
  const load = async () => { const { data } = await api.get('/sms-tiers'); setTiers(data); };
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    try {
      await api.put(`/admin/sms-tiers/${t.tier}`, {
        island: t.tier,
        price_nzd: parseFloat(t.price_nzd),
        label: t.label,
        description: t.description,
        active: t.active,
      });
      toast.success(`${t.tier === 'north' ? 'North' : 'South'} Island pricing saved`);
      load();
    } catch { toast.error('Save failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="card bg-orange-50/50 border-orange-200">
        <p className="text-sm text-orange-900">
          <strong>SMS Pricing (per island):</strong> Set a flat monthly price for each island. Subscribers pay this fee to receive SMS
          for <em>all alert levels</em> (Info + Urgent + Critical) on their subscribed highways within that island.
          Users can subscribe to <strong>one island or both simultaneously</strong>.
        </p>
      </div>
      {tiers.map((t) => {
        const emoji = t.tier === 'north' ? '🌋' : '🏔️';
        const label = t.tier === 'north' ? 'North Island' : 'South Island';
        return (
          <div key={t.tier} className="card" data-testid={`tier-${t.tier}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{emoji}</span>
              <div className="font-display font-bold text-lg">{label}</div>
              <label className="ml-auto text-xs flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={t.active}
                  onChange={(e) => setTiers(tiers.map((x) => x.tier === t.tier ? { ...x, active: e.target.checked } : x))}
                  className="accent-teal-500"
                  data-testid={`tier-active-${t.tier}`}
                /> Available for purchase
              </label>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label">Price (NZD/month)</label>
                <input
                  type="number" step="0.01" min="0" className="input"
                  value={t.price_nzd}
                  onChange={(e) => setTiers(tiers.map((x) => x.tier === t.tier ? { ...x, price_nzd: e.target.value } : x))}
                  data-testid={`tier-price-${t.tier}`}
                />
              </div>
              <div>
                <label className="label">Display label</label>
                <input
                  className="input"
                  value={t.label || ''}
                  onChange={(e) => setTiers(tiers.map((x) => x.tier === t.tier ? { ...x, label: e.target.value } : x))}
                />
              </div>
              <div>
                <label className="label">Description (shown to users)</label>
                <input
                  className="input"
                  value={t.description || ''}
                  onChange={(e) => setTiers(tiers.map((x) => x.tier === t.tier ? { ...x, description: e.target.value } : x))}
                />
              </div>
            </div>
            <div className="mt-4">
              <button onClick={() => save(t)} className="btn btn-primary" data-testid={`tier-save-${t.tier}`}>Save {label}</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
